import * as angular from "angular";
import type { JsonSchemaDefinition } from "../../../../../../../extensions/json-schema/runtime";
import type { FormHandle } from "../../../../../../../extensions/json-schema/FormHandle";
import type {
	FormValidationRequest,
	FormValidationResult
} from "../../../../../../../extensions/json-schema/components/form-session";
import entitySchemaTab from "../entity-schema-tab";
import template from "./entity-schema-editor.html";

interface SectionAction {
	code: string;
}

export interface EntitySchemaEditorController {
	schema: JsonSchemaDefinition;

	model: unknown;

	readonly: boolean;

	onSave: (args: { model: unknown }) => unknown | PromiseLike<unknown>;

	section: {
		activeActions: SectionAction[];
		invokeAction(action: SectionAction): unknown;
	};
}

// AngularJS owns the merged binding and required-controller properties.
// eslint-disable-next-line no-redeclare
export class EntitySchemaEditorController implements angular.IController {
	public static $inject = ["$scope", "$logger"];

	public schemaForm?: Pick<angular.IFormController, "$invalid">;

	public validationResult?: FormValidationResult;

	public editModel: unknown;

	public formControl?: FormHandle;

	public isLoading = false;

	private editSnapshot: unknown;

	public constructor(
		private readonly $scope: angular.IScope,
		private readonly $logger: { error(message: string, error: unknown): void }
	) {}

	public $onInit(): void {
		this.syncState();
	}

	public $onChanges(changes: angular.IOnChangesObject): void {
		const change = changes.model;

		if(!change || change.isFirstChange()) {
			return;
		}

		if(!this.isDirty()) {
			this.syncState(change.currentValue);
		}
	}

	public beginEditing(): boolean {
		if(this.readonly) {
			return false;
		}

		this.syncState();

		return true;
	}

	public leaveEditing(): boolean {
		this.editModel = angular.copy(this.editSnapshot);
		this.formControl?.reset(this.editModel);
		this.validationResult = undefined;

		return true;
	}

	public isDirty(): boolean {
		return !angular.equals(this.editModel, this.editSnapshot);
	}

	public handleSubmit(): unknown {
		const action = this.section.activeActions.find(candidate => candidate.code === "save");

		return action ? this.section.invokeAction(action) : false;
	}

	public save(): Promise<boolean> {
		return this.saveChanges();
	}

	public async saveChanges(): Promise<boolean> {
		if(this.readonly) {
			return false;
		}

		if(!this.isDirty()) {
			return true;
		}

		let candidate: unknown;
		let rendered = false;
		const request: FormValidationRequest = {
			onValidated: (value, result) => {
				rendered = true;
				this.validationResult = result;

				candidate = angular.copy(value);
				this.editModel = angular.copy(value);
			}
		};
		this.$scope.$broadcast("schemaFormValidate", request);

		if(!rendered) {
			this.validationResult = {
				valid: false,
				messages: ["This form cannot be validated."]
			};
		}

		if(!rendered || !this.validationResult?.valid) {
			return false;
		}

		this.isLoading = true;

		try {
			await this.onSave({ model: angular.copy(candidate) });
			this.editSnapshot = angular.copy(this.editModel);

			return true;
		}catch(error) {
			this.$logger.error("entity schema update error", error);
			throw error;
		}finally {
			this.isLoading = false;
		}
	}

	private syncState(model = this.model): void {
		this.validationResult = undefined;
		this.editModel = angular.copy(model);
		this.editSnapshot = angular.copy(this.editModel);
		this.formControl?.reset(this.editModel);
	}
}

export default angular.module("vesl.components.entity-schema-editor", [entitySchemaTab])
	.component("entitySchemaEditor", {
		controller: EntitySchemaEditorController,
		template,
		bindings: {
			actionLabel: "<",
			schema: "<",
			model: "<",
			readonly: "<",
			onSave: "&"
		},
		require: { section: "^^viewModesSection" }
	})
	.name;
